// Main App Page - Unified Single Page Architecture
// One page to rule them all - all views are states, not routes

import { useEffect, useRef, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { batchGetCells, getExplorerProducts, getProductList, trackCellView, trackSearch } from '../../modules/dataService.js'
import { transformExplorerItems } from '../../modules/explorerCellShape.js'
import {
    appViewMode,
    isSingleCellMode
} from '../../state/appViewMode.js'
import {
    agingRange,
    batchVariationRange,
    capacityRange,
    currentRange,
    energyRange,
    fullRangeCapacity,
    fullRangeCurrent,
    fullRangeAging,
    fullRangeBatchVariation,
    fullRangeEnergy,
    fullRangePower,
    fullRangeYear,
    powerRange,
    savedSearchTerms,
    searchQuery,
    ionCellIdSet,
    selectedAnodeChemistries,
    selectedCathodeChemistries,
    selectedChemistries,
    selectedCountries,
    selectedFormFactors,
    selectedManufacturers,
    selectedTechnologies,
    yearRange
} from '../../state/filterStore.js'
import { allCells, basis, domain, filteredCells, filteredDemoCount, layoutMode } from '../../state/globalSignals.js'
import { demoUnlockVersion, isDemoCatalogUnlocked } from '../../state/demoLimitStore.js'
import { isAuthenticated, subscriptionTier } from '../../state/userState.js'
import { registerCell } from '../../utils/cellSlugMap.js'
import { cellSlug as getCellSlug, findCellBySlug } from '../../utils/cellSlug.js'

/**
 * Count of "accessible" cells for the demo-count / pagination pill. DEMO users
 * have the full catalog unlocked (daily view quota, not per-cell locks), so
 * every cell counts; other tiers count only the non-locked (demo) cells.
 */
function countUnlockedCells(cells) {
  if (!Array.isArray(cells)) return 0
  if (isDemoCatalogUnlocked()) return cells.length
  return cells.filter((c) => !c.is_locked).length
}

// Layout Components
import AdaptiveSidebar from '../layout/AdaptiveSidebar.jsx'
import UnifiedContextBar from '../layout/UnifiedContextBar.jsx'

// View Components
import CellPage from '../../pages/CellPage.jsx'
import BenchmarkingView from '../benchmarking/BenchmarkingView.jsx'
import FreePlotView from '../benchmarking/FreePlotView.jsx'
import CompareTableView from '../compare/views/CompareTableView.jsx'
import { showToast } from '../shared/Toast.jsx'
import RadarOverviewPage from '../radar/RadarOverviewPage.jsx'
import RadarSectionPage from '../radar/RadarSectionPage.jsx'
import ActiveFiltersHeader from './ActiveFiltersHeader.jsx'
import GridView from './GridView.jsx'
import CellSelectionEntry from '../chat/CellSelectionEntry.jsx'
import { GUIDED_SELECTION_ENABLED } from '../../config/featureFlags.js'
import TableView from './TableView.jsx'

export default function MainAppPage() {
  const [location, setLocation] = useLocation()
  const [isLoading, setIsLoading] = useState(true)
  const [loadingError, setLoadingError] = useState(null)

  // Get search params separately (wouter doesn't expose them in location)
  const [searchParams, setSearchParams] = useState(window.location.search)

  // Prime the DEMO daily view-limit meter with the authoritative summary. Re-runs
  // when the auth/tier signals settle: on a fresh tab subscriptionTier is still
  // 'free' at mount (auth fetch pending), so a mount-only fetch no-ops on the
  // isDemoCatalogUnlocked guard and the meter shows the DEFAULT_DEMO_LIMIT
  // fallback ("0 of 10") instead of the real cap. Reacting to subscriptionTier
  // fetches the true limit the moment it flips to 'DEMO'. Self-guards for non-demo
  // users; deps are signal values that change only on real auth transitions (no
  // render loop). Per-request X-Demo-Views-* headers keep it live afterwards.
  useEffect(() => {
    import('../../services/demoLimitService.js')
      .then(({ refreshDemoUsageSummary }) => refreshDemoUsageSummary())
      .catch(() => { /* meter degrades to header/localStorage — non-fatal */ })
  }, [isAuthenticated.value, subscriptionTier.value])

  // Watch for URL changes including query params
  useEffect(() => {
    const handleUrlChange = () => {
      const newSearch = window.location.search
      if (newSearch !== searchParams) {
        setSearchParams(newSearch)
      }
    }

    // Check every 50ms for URL changes
    const interval = setInterval(handleUrlChange, 50)
    return () => clearInterval(interval)
  }, [searchParams])

  const mode = appViewMode.value
  const layout = layoutMode.value
  const isSingleCell = isSingleCellMode.value

  // Debug log
  console.log('[MainAppPage] Render:', { mode, layout, isSingleCell, location, searchParams })

  // Update body data-view-mode and data-layout-mode attributes for CSS targeting
  useEffect(() => {
    const viewMode = layout === 'compare' ? 'compare' : mode
    document.body.setAttribute('data-view-mode', viewMode)
    document.body.setAttribute('data-layout-mode', layout)
    return () => {
      document.body.removeAttribute('data-view-mode')
      document.body.removeAttribute('data-layout-mode')
    }
  }, [mode, layout])

  // Load (or reload) the explorer cell list — LAZY LOADING VERSION.
  // Uses /explorer/v2 endpoint which returns paginated data with KPIs instead
  // of loading all cell details upfront. Hoisted out of the mount effect so it
  // can be REUSED (not duplicated) by the post-unlock reactive reload effect
  // below — `getExplorerProducts` uses `cache:false`, so calling this again
  // after an in-place unlock returns fresh, un-gated data for today's
  // unlocked cells. Closes only over stable setters + module-level imports,
  // so re-defining it each render is safe (no stale-closure risk).
  function loadExplorerCells() {
    console.log('[MainAppPage] Loading cells (LAZY LOADING v2)...')
    setIsLoading(true)

    // Transform explorer items to the nested cell shape. Shared with the
    // BATEMO ION chat Ragone plot via modules/explorerCellShape.js so both
    // seed the catalogue identically (single source of truth — no drift).
    const transformItems = transformExplorerItems

    // Load all pages of explorer products
    async function loadAllPages() {
      try {
        // First request to get total count and first batch
        const firstResult = await getExplorerProducts({
          page: 1,
          limit: 50,  // Backend max is 50
          sort_by: 'name',
          sort_order: 'asc'
        })

        console.log('[MainAppPage] Got explorer products:', firstResult)

        if (!firstResult?.items?.length) {
          console.warn('[MainAppPage] No products from /explorer/v2, falling back to legacy...')
          return fallbackToLegacyLoad()
        }

        let allItems = [...firstResult.items]
        const totalPages = firstResult.pages || 1

        console.log('[MainAppPage] Pagination: page 1/', totalPages, 'total:', firstResult.total)

        // Load remaining pages in parallel (batches of 3 to avoid overwhelming server)
        if (totalPages > 1) {
          const pagesToLoad = []
          for (let page = 2; page <= totalPages; page++) {
            pagesToLoad.push(page)
          }

          console.log('[MainAppPage] Loading', pagesToLoad.length, 'more pages...')

          // Load pages in batches of 3
          const batchSize = 3
          for (let i = 0; i < pagesToLoad.length; i += batchSize) {
            const batch = pagesToLoad.slice(i, i + batchSize)
            const batchResults = await Promise.all(
              batch.map(page =>
                getExplorerProducts({
                  page,
                  limit: 50,
                  sort_by: 'name',
                  sort_order: 'asc'
                })
              )
            )

            for (const result of batchResults) {
              if (result?.items?.length) {
                allItems = allItems.concat(result.items)
              }
            }

            console.log('[MainAppPage] Loaded pages', batch.join(','), '- total items:', allItems.length)
          }
        }

        // Transform all items
        const cellsArray = transformItems(allItems)

        console.log('[MainAppPage] ✓ Transformed', cellsArray.length, 'cells from explorer endpoint')
        console.log('[MainAppPage] First cell sample:', {
          id: cellsArray[0]?.id,
          name: cellsArray[0]?.name,
          manufacturer: cellsArray[0]?.manufacturer,
          E_c10_Wh: cellsArray[0]?.electrical?.E_c10_Wh,
          P_peak_W: cellsArray[0]?.electrical?.P_peak_W
        })

        // Register cells for slug mapping
        cellsArray.forEach(cell => {
          if (cell.id && cell.manufacturer && cell.name) {
            registerCell(cell.id, { manufacturer: cell.manufacturer, name: cell.name })
          }
        })

        allCells.value = cellsArray
        filteredCells.value = cellsArray
        filteredDemoCount.value = countUnlockedCells(cellsArray)
        console.log('[MainAppPage] ✅ Set allCells and filteredCells to', cellsArray.length, 'cells (LAZY LOADING)')
        setIsLoading(false)

      } catch (error) {
        console.warn('[MainAppPage] Explorer endpoint failed, falling back to legacy:', error)
        return fallbackToLegacyLoad()
      }
    }

    loadAllPages()

    // Fallback to legacy loading method (loads all cell details)
    async function fallbackToLegacyLoad() {
      console.log('[MainAppPage] Using legacy loading (batchGetCells)...')

      try {
        const productList = await getProductList()
        const products = productList?.products || []

        if (!Array.isArray(products) || products.length === 0) {
          console.error('[MainAppPage] No products found')
          allCells.value = []
          filteredCells.value = []
          filteredDemoCount.value = 0
          setIsLoading(false)
          return
        }

        const cellIds = products.map(p => p.product_id || p.id || p).filter(id => typeof id === 'string' && id.length > 0)

        if (cellIds.length === 0) {
          console.error('[MainAppPage] No valid cell IDs found')
          allCells.value = []
          filteredCells.value = []
          filteredDemoCount.value = 0
          setIsLoading(false)
          return
        }

        console.log('[MainAppPage] Legacy loading', cellIds.length, 'cells...')
        const cellsMap = await batchGetCells(cellIds, {}, 10)
        const cellsArray = Array.from(cellsMap.values())

        cellsArray.forEach(cell => {
          if (cell.id && cell.manufacturer && cell.name) {
            registerCell(cell.id, { manufacturer: cell.manufacturer, name: cell.name })
          }
        })

        allCells.value = cellsArray
        filteredCells.value = cellsArray
        filteredDemoCount.value = countUnlockedCells(cellsArray)
        console.log('[MainAppPage] ✅ Legacy load complete:', cellsArray.length, 'cells')
        setIsLoading(false)
      } catch (err) {
        console.error('[MainAppPage] Legacy load failed:', err)
        allCells.value = []
        filteredCells.value = []
        filteredDemoCount.value = 0
        setLoadingError(err.message || 'Failed to load cell data')
        setIsLoading(false)
      }
    }
  }

  // Load all cells on mount - LAZY LOADING VERSION
  // Skip cell loading for radar mode — radar fetches its own data
  useEffect(() => {
    if (layout === 'radar') {
      setIsLoading(false)
      return
    }
    loadExplorerCells()
  }, [])

  // Reactive post-unlock reload (demo scraping-friction, Task 10b): after an
  // in-place unlock on Grid/Table/Plot, `unlockCell` invalidates the explorer
  // cache and bumps `demoUnlockVersion` but does NOT itself reload allCells/
  // filteredCells — so the newly-unlocked cell keeps showing teaser values
  // until the next full reload. This effect watches that signal and re-runs
  // the SAME load routine above (reused, not duplicated) so the exact
  // premium values land for Grid/Table/Plot alike.
  //
  // Guards:
  //   - `prevUnlockVersionRef` starts at the CURRENT version, so the effect's
  //     unavoidable first run-on-mount is a no-op (version === ref) — no
  //     double-load alongside the mount effect above.
  //   - Only demo users (isDemoCatalogUnlocked()) trigger a reload.
  //   - Skipped in radar layout (radar owns its own data).
  //   - loadExplorerCells() never bumps demoUnlockVersion itself (it doesn't
  //     call unlockCell/applyUnlockResult), so this can't loop.
  const prevUnlockVersionRef = useRef(demoUnlockVersion.value)
  useEffect(() => {
    const version = demoUnlockVersion.value
    if (version === prevUnlockVersionRef.current) return
    prevUnlockVersionRef.current = version
    if (!isDemoCatalogUnlocked()) return
    if (layout === 'radar') return
    console.log('[MainAppPage] Reloading explorer cells after unlock (demoUnlockVersion:', version, ')')
    loadExplorerCells()
  }, [demoUnlockVersion.value])

  // Apply filters whenever filter signals change
  useEffect(() => {
    if (allCells.value.length === 0) {
      console.log('[MainAppPage] Filter effect - no cells yet, skipping')
      return
    }

    const currentBasis = basis.value
    const currentDomain = domain.value

    // Log range values to debug timing issues
    console.log('[MainAppPage] 🔍 Applying filters - basis:', currentBasis, 'domain:', currentDomain)
    console.log('[MainAppPage] 🔍 Range values:', {
      energy: energyRange.value,
      fullEnergy: fullRangeEnergy.value,
      power: powerRange.value,
      fullPower: fullRangePower.value
    })
    console.log('[MainAppPage] 🔍 Active filters:', {
      cathodeChemistries: selectedCathodeChemistries.value,
      anodeChemistries: selectedAnodeChemistries.value,
      manufacturers: selectedManufacturers.value,
      formats: selectedFormFactors.value,
      technologies: selectedTechnologies.value
    })

    // Ghost-aware filtering:
    //   - Visible-on-ghost dims (manufacturer, format, energy/power/capacity/current, search):
    //     filter applies to demo + locked cells alike.
    //   - Hidden-on-ghost dims (chemistry, technology, country, year, batch, aging):
    //     locked cells are excluded entirely the moment any such filter activates,
    //     because the user cannot reason about a match they cannot see on the card.
    const demoCells = allCells.value.filter(c => !c.is_locked)
    const lockedCells = allCells.value.filter(c => c.is_locked)

    const isYearActive = yearRange.value.min > fullRangeYear.value.min || yearRange.value.max < fullRangeYear.value.max
    const isBatchVarActive = batchVariationRange.value.min > fullRangeBatchVariation.value.min || batchVariationRange.value.max < fullRangeBatchVariation.value.max
    const isAgingActive = agingRange.value.min > fullRangeAging.value.min || agingRange.value.max < fullRangeAging.value.max

    const hasHiddenFilterActive =
      selectedCathodeChemistries.value.length > 0 ||
      selectedAnodeChemistries.value.length > 0 ||
      selectedChemistries.value.length > 0 ||
      selectedTechnologies.value.length > 0 ||
      selectedCountries.value.length > 0 ||
      isYearActive ||
      isBatchVarActive ||
      isAgingActive

    // Hidden-on-ghost filters (chemistry/technology/country/year/batch/aging) normally
    // drop locked cells so a free-tier user can't "match" a value they can't see on the
    // card. But a DEMO user's catalogue is fully unlocked and STILL surfaces
    // chemistry/format/base KPIs on locked (shadow) cells (only impedance/thermal/quality
    // are gated), so for demo we keep them — otherwise a chemistry preset collapses the
    // plot/grid to the ~30-cell demo sample instead of the full catalogue (Review: the
    // guided chemistry-filter leak; sibling of the facet-count fix).
    const excludeLockedUnderHiddenFilter = hasHiddenFilterActive && !isDemoCatalogUnlocked()
    let filtered = excludeLockedUnderHiddenFilter ? [...demoCells] : [...demoCells, ...lockedCells]
    console.log('[MainAppPage] Starting with', filtered.length, 'cells (hiddenFilterActive:', hasHiddenFilterActive, ', excludeLocked:', excludeLockedUnderHiddenFilter, ', demo:', demoCells.length, ', locked:', lockedCells.length, ')')

    // Ion's explicit id set (D4). Applied FIRST because it is a cheap Set lookup
    // that can cut 300 cells to 5, so every predicate below runs on a fraction of
    // the list. It is an INTERSECTION, not a replacement: a facet the user ticks
    // afterwards narrows Ion's set further, which is what "these cells, and now
    // only the LFP ones among them" should do.
    //
    // `null` (inactive) and an empty Set are deliberately different: only the
    // former skips the filter. An empty set means "no cells match", which is a
    // legitimate answer, not a reason to show everything.
    if (ionCellIdSet.value) {
      const ids = ionCellIdSet.value
      filtered = filtered.filter((cell) => ids.has(cell.id))
    }

    // Chemistry filters: Cathode, Anode, and "Analysis Ongoing"
    // Logic: (Cathode AND Anode) OR "Analysis Ongoing"
    // This allows "Analysis Ongoing" to act as an ADDITIVE filter, not restrictive
    const hasCathodeFilter = selectedCathodeChemistries.value.length > 0
    const hasAnodeFilter = selectedAnodeChemistries.value.length > 0
    const hasAnalysisOngoingFilter = selectedChemistries.value.includes('Analysis Ongoing')

    // Helper to check if a cell is "Analysis Ongoing"
    const isAnalysisOngoingCell = (cell) => {
      const checkOngoing = (val) => val && val.toLowerCase().includes('analysis ongoing')
      const cathodeGroup = cell.chemical?.cathode_material_group || cell.chemical?.main_chemistry || cell.chemical?.cathode
      const anodeGroup = cell.chemical?.anode_material_group || cell.chemical?.anode
      const result = checkOngoing(cathodeGroup) || checkOngoing(anodeGroup) ||
             checkOngoing(cell.chemical?.main_chemistry) || checkOngoing(cell.chemical?.anode)
      // Debug erste 5 Analysis Ongoing Zellen
      if (result && ongoingDebugCount < 5) {
        console.log('[MainAppPage] Found Analysis Ongoing cell:', {
          name: cell.name,
          cathodeGroup,
          anodeGroup,
          main_chemistry: cell.chemical?.main_chemistry
        })
        ongoingDebugCount++
      }
      return result
    }

    let ongoingDebugCount = 0

    // Apply combined chemistry filter if any chemistry filter is active
    if (hasCathodeFilter || hasAnodeFilter || hasAnalysisOngoingFilter) {
      const beforeCount = filtered.length
      console.log('[MainAppPage] Chemistry filters - hasCathode:', hasCathodeFilter, 'hasAnode:', hasAnodeFilter, 'hasAnalysisOngoing:', hasAnalysisOngoingFilter)

      let ongoingCount = 0
      let cathodeCount = 0
      let anodeCount = 0

      filtered = filtered.filter(cell => {
        // Check if cell matches "Analysis Ongoing" filter
        if (hasAnalysisOngoingFilter && isAnalysisOngoingCell(cell)) {
          ongoingCount++
          console.log('[MainAppPage] 🟢 Analysis Ongoing cell:', cell.name)
          return true  // Include analysis ongoing cells regardless of cathode/anode selection
        }

        // Check Cathode filter (if active)
        if (hasCathodeFilter) {
          const cathodeGroup = cell.chemical?.cathode_material_group ||
                              cell.chemical?.main_chemistry ||
                              cell.chemical?.cathode
          if (!cathodeGroup || !selectedCathodeChemistries.value.includes(cathodeGroup)) {
            return false  // Doesn't match cathode filter
          }
          cathodeCount++
        }

        // Check Anode filter (if active)
        if (hasAnodeFilter) {
          const anodeGroup = cell.chemical?.anode_material_group || cell.chemical?.anode
          if (!anodeGroup || !selectedAnodeChemistries.value.includes(anodeGroup)) {
            return false  // Doesn't match anode filter
          }
          anodeCount++
        }

        // If we get here, either no cathode/anode filters are active, or cell matches both
        // But we need to exclude "Analysis Ongoing" cells from cathode/anode results
        // unless they were explicitly selected
        if (!hasAnalysisOngoingFilter && isAnalysisOngoingCell(cell)) {
          return false  // Don't include analysis ongoing cells if not explicitly selected
        }

        return true
      })
      console.log('[MainAppPage] Chemistry filter (cathode/anode/ongoing):', beforeCount, '→', filtered.length, 'cells (ongoing:', ongoingCount, 'cathode:', cathodeCount, 'anode:', anodeCount, ')')
    }

    // Manufacturer filter (normalized data: top-level manufacturer)
    if (selectedManufacturers.value.length > 0) {
      filtered = filtered.filter(cell => {
        if (!cell.manufacturer) return false  // Exclude cells without manufacturer when filter is active
        return selectedManufacturers.value.includes(cell.manufacturer)
      })
    }

    // Country filter (normalized data: general.origin_country)
    if (selectedCountries.value.length > 0) {
      filtered = filtered.filter(cell => {
        const cellCountry = cell.general?.origin_country || cell.country
        if (!cellCountry) return false  // Exclude cells without country when filter is active
        return selectedCountries.value.includes(cellCountry)
      })
    }

    // Format filter (normalized data: mechanical.format_group)
    if (selectedFormFactors.value.length > 0) {
      filtered = filtered.filter(cell => {
        const cellFormat = cell.mechanical?.format_group || cell.mechanical?.format
        return selectedFormFactors.value.includes(cellFormat)
      })
    }

    // Technology filter (normalized data: top-level technology)
    if (selectedTechnologies.value.length > 0) {
      filtered = filtered.filter(cell => {
        const cellTech = cell.technology || cell.chemical?.main_chemistry || cell.chemical?.cathode
        if (!cellTech) return false  // Exclude cells without technology when filter is active
        return selectedTechnologies.value.includes(cellTech)
      })
    }

    // Energy range filter - BASIS AWARE (abs, grav, vol)
    // NOTE: Normalized data has electrical.* fields in UI units (Wh, Ah, W, etc.)
    const energy = energyRange.value
    const fullEnergy = fullRangeEnergy.value

    // Only apply energy filter if user has modified the range from full range
    const energyFilterActive = energy.min > fullEnergy.min || energy.max < fullEnergy.max
    console.log('[MainAppPage] Energy filter check:', {
      basis: currentBasis,
      userRange: energy,
      fullRange: fullEnergy,
      filterActive: energyFilterActive
    })

    if (energyFilterActive) {
      const beforeCount = filtered.length
      filtered = filtered.filter(cell => {
        let cellEnergy

        if (currentBasis === 'abs') {
          // Absolute: E_c10_Wh (already in Wh)
          cellEnergy = cell.electrical?.E_c10_Wh
        } else if (currentBasis === 'grav') {
          // Gravimetric: E_grav_Wh_per_kg (already in Wh/kg)
          cellEnergy = cell.electrical?.E_grav_Wh_per_kg
        } else if (currentBasis === 'vol') {
          // Volumetric: E_vol_Wh_per_L (already in Wh/L)
          cellEnergy = cell.electrical?.E_vol_Wh_per_L
        }

        if (cellEnergy == null) return true // Keep cells without data
        return cellEnergy >= energy.min && cellEnergy <= energy.max
      })
      console.log('[MainAppPage] Energy filter applied:', beforeCount, '→', filtered.length, 'cells')
    }

    // Power range filter - BASIS AWARE (abs, grav, vol)
    const power = powerRange.value
    const fullPower = fullRangePower.value
    const powerFilterActive = power.min > fullPower.min || power.max < fullPower.max

    if (powerFilterActive) {
      filtered = filtered.filter(cell => {
        let cellPower

        if (currentBasis === 'abs') {
          // Absolute: P_peak_W (already in W)
          cellPower = cell.electrical?.P_peak_W
        } else if (currentBasis === 'grav') {
          // Gravimetric: P_grav_W_per_kg (already in W/kg)
          cellPower = cell.electrical?.P_grav_W_per_kg
        } else if (currentBasis === 'vol') {
          // Volumetric: P_vol_W_per_L (already in W/L)
          cellPower = cell.electrical?.P_vol_W_per_L
        }

        if (cellPower == null) return true // Keep cells without data
        return cellPower >= power.min && cellPower <= power.max
      })
    }

    // Capacity range filter - BASIS AWARE (abs, grav, vol)
    const capacity = capacityRange.value
    const fullCapacity = fullRangeCapacity.value
    const capacityFilterActive = capacity.min > fullCapacity.min || capacity.max < fullCapacity.max

    if (capacityFilterActive) {
      filtered = filtered.filter(cell => {
        let cellCapacity

        if (currentBasis === 'abs') {
          // Absolute: C_c10_Ah (already in Ah)
          cellCapacity = cell.electrical?.C_c10_Ah
        } else if (currentBasis === 'grav') {
          // Gravimetric: C_grav_Ah_per_kg (already in Ah/kg)
          cellCapacity = cell.electrical?.C_grav_Ah_per_kg
        } else if (currentBasis === 'vol') {
          // Volumetric: C_vol_Ah_per_L (already in Ah/L)
          cellCapacity = cell.electrical?.C_vol_Ah_per_L
        }

        if (cellCapacity == null) return true // Keep cells without data
        return cellCapacity >= capacity.min && cellCapacity <= capacity.max
      })
    }

    // Current range filter - BASIS AWARE (abs, grav, vol)
    const current = currentRange.value
    const fullCurrent = fullRangeCurrent.value
    const currentFilterActive = current.min > fullCurrent.min || current.max < fullCurrent.max

    if (currentFilterActive) {
      filtered = filtered.filter(cell => {
        let cellCurrent

        if (currentBasis === 'abs') {
          // Absolute: I_peak_A (already in A)
          cellCurrent = cell.electrical?.I_peak_A
        } else if (currentBasis === 'grav') {
          // Gravimetric: I_grav_A_per_kg (already in A/kg)
          cellCurrent = cell.electrical?.I_grav_A_per_kg
        } else if (currentBasis === 'vol') {
          // Volumetric: I_vol_A_per_L (already in A/L)
          cellCurrent = cell.electrical?.I_vol_A_per_L
        }

        if (cellCurrent == null) return true // Keep cells without data
        return cellCurrent >= current.min && cellCurrent <= current.max
      })
    }

    // Year range filter (normalized data: top-level year field)
    const year = yearRange.value
    const fullYear = fullRangeYear.value
    const yearFilterActive = year.min > fullYear.min || year.max < fullYear.max
    if (yearFilterActive) {
      filtered = filtered.filter(cell => {
        // Use year field directly if available
        if (cell.year && typeof cell.year === 'number') {
          return cell.year >= year.min && cell.year <= year.max
        }
        // Fallback: parse from date string
        if (!cell.date) return true  // Keep cells without date
        const cellYear = parseInt(cell.date.split('-')[0])
        if (isNaN(cellYear)) return true  // Keep cells with invalid date
        return cellYear >= year.min && cellYear <= year.max
      })
    }

    // Batch Variation range filter (normalized data: batch.CAP_spread_std_rel in %)
    const batchVar = batchVariationRange.value
    const fullBatchVar = fullRangeBatchVariation.value
    const batchVarFilterActive = batchVar.min > fullBatchVar.min || batchVar.max < fullBatchVar.max
    if (batchVarFilterActive) {
      filtered = filtered.filter(cell => {
        const cellBatchVar = (cell.batch?.CAP_spread_std_rel || 0) * 100
        return cellBatchVar >= batchVar.min && cellBatchVar <= batchVar.max
      })
    }

    // Initial Aging range filter (normalized data: aging.CAP_loss_rel in %)
    const aging = agingRange.value
    const fullAging = fullRangeAging.value
    const agingFilterActive = aging.min > fullAging.min || aging.max < fullAging.max
    if (agingFilterActive) {
      filtered = filtered.filter(cell => {
        const cellAging = Math.abs((cell.aging?.CAP_loss_rel || 0) * 100)
        return cellAging >= aging.min && cellAging <= aging.max
      })
    }

    // Search filter: OR logic - show cell if it matches ANY search term or current query
    // Combine current query with saved search terms
    // SEARCH FILTER - Combine live query + saved terms with OR logic
    // Live query: tokenized AND logic ("murata vtc4" → both words must match)
    // Saved terms: AND logic within each term
    // Between all: OR logic (matches live query OR any saved term)
    const currentQuery = (searchQuery.value || '').trim().toLowerCase()
    const savedTerms = savedSearchTerms.value || []

    if (currentQuery !== '' || savedTerms.length > 0) {
      const beforeCount = filtered.length

      filtered = filtered.filter(cell => {
        // Locked cells only match against fields visible on the card (name + manufacturer).
        // Searching demo cells additionally matches chemistry.
        const chemistryPart = cell.is_locked
          ? ''
          : (cell.chemical?.main_chemistry || cell.chemical?.cathode || '')
        const searchStr = `${cell.name} ${cell.manufacturer} ${chemistryPart}`.toLowerCase()

        // Check live query (AND logic within tokens)
        let matchesLiveQuery = false
        if (currentQuery !== '') {
          const tokens = currentQuery.split(/\s+/).filter(t => t.length > 0)
          matchesLiveQuery = tokens.every(token => searchStr.includes(token))
        }

        // Check saved terms (OR logic between terms, AND within each term)
        const matchesSavedTerms = savedTerms.some(term => {
          const termTokens = term.toLowerCase().split(/\s+/).filter(t => t.length > 0)
          return termTokens.every(token => searchStr.includes(token))
        })

        // OR logic: matches if live query matches OR any saved term matches
        return matchesLiveQuery || matchesSavedTerms
      })
      console.log('[MainAppPage] Search filter (OR):', { liveQuery: currentQuery, savedTerms }, '→', beforeCount, '→', filtered.length, 'cells')
    }

    const finalDemo = countUnlockedCells(filtered)
    const finalLocked = filtered.length - finalDemo
    console.log(`[MainAppPage] ✅ Final filtered: ${finalDemo} demo + ${finalLocked} locked / ${allCells.value.length} total`)
    filteredDemoCount.value = finalDemo
    // New array reference so signal subscribers (plots etc.) detect the change.
    filteredCells.value = [...filtered]
  }, [
    allCells.value,
    basis.value,
    domain.value,
    selectedCathodeChemistries.value,
    selectedAnodeChemistries.value,
    selectedChemistries.value,
    selectedManufacturers.value,
    selectedCountries.value,
    selectedFormFactors.value,
    selectedTechnologies.value,
    energyRange.value,
    powerRange.value,
    capacityRange.value,
    currentRange.value,
    yearRange.value,
    batchVariationRange.value,
    agingRange.value,
    searchQuery.value,
    savedSearchTerms.value,
    ionCellIdSet.value,
    fullRangeEnergy.value,
    fullRangePower.value,
    fullRangeCapacity.value,
    fullRangeCurrent.value,
    fullRangeYear.value,
    fullRangeBatchVariation.value,
    fullRangeAging.value
  ])

  // Debounced search tracking - track after user stops typing for 1 second
  const searchTrackingTimeout = useRef(null)
  useEffect(() => {
    const query = searchQuery.value?.trim()
    if (query && query.length >= 2) {
      clearTimeout(searchTrackingTimeout.current)
      searchTrackingTimeout.current = setTimeout(() => {
        trackSearch(query, filteredCells.value?.length || 0)
      }, 1000) // 1 second debounce
    }
    return () => clearTimeout(searchTrackingTimeout.current)
  }, [searchQuery.value])

  // Handle cell click from any view
  const handleCellClick = (cardElement, cell) => {
    console.log('[MainAppPage] Cell clicked:', cell.id)

    // Track cell view in Plausible Analytics
    trackCellView(cell.id, `${cell.manufacturer || ''} ${cell.name || ''}`.trim(), mode)

    // Save current view to localStorage for back navigation
    localStorage.setItem('previousCellsView', mode)
    console.log('[MainAppPage] Saved previous view:', mode)

    // Generate slug
    const slug = getCellSlug(cell)

    // Navigate to cell detail (sidebar will auto-collapse via useEffect)
    setLocation(`/explorer?cell=${slug}&section=factsheet`)
  }

  // Handle locked (shadow) cell click — show toast instead of navigating
  const handleLockedCellClick = (cardElement, cell) => {
    showToast(
      'This cell is not included in your plan.',
      'Upgrade',
      '/plans'
    )
  }

  // Check for ?cell= query param - compute from location (triggers re-render on URL change)
  const url = new URL(window.location.href)
  const cellParam = url.searchParams.get('cell')
  const sectionParam = url.searchParams.get('section') || 'factsheet'

  // Force re-computation when location changes by referencing it
  const _locationDep = location

  console.log('[MainAppPage] URL params extracted:', { cellParam, sectionParam, href: window.location.href })

  // Control sidebar visibility based on view mode and screen size
  useEffect(() => {
    import('../../state/globalSignals.js').then(({ sidebarVisible }) => {
      if (cellParam) {
        // Single-cell view: only collapse on narrow screens (< 1600px)
        const isNarrowScreen = window.innerWidth < 1600
        sidebarVisible.value = !isNarrowScreen
      } else {
        // Grid/Table/Graph view: always show sidebar
        sidebarVisible.value = true
      }

      // Remove any inline styles that may have been set
      const sidebar = document.querySelector('.adaptive-sidebar')
      if (sidebar) {
        sidebar.style.transform = ''
        sidebar.style.opacity = ''
        sidebar.style.transition = ''
      }
    })
  }, [cellParam])

  return (
    <div className="main-app-page" data-view-mode={cellParam ? 'single-cell' : (layout === 'compare' ? 'compare' : mode)}>
      {/* CommandBar is rendered by AppShell, not here */}

      {/* Layer 1: Unified Context Bar - ADAPTIVE */}
      <UnifiedContextBar />

      {/* Layer 2: Content Wrapper (Sidebar + Main Content) */}
      {/* data-tour anchor for ion-intro's `intro-drive` step (I2) — spans
          both the sidebar filters and the grid, so the overlay's cutout
          keeps the whole live-reflow spectacle visible instead of dimming
          the grid behind 65% black while Ion drives it. */}
      <div className="app-content-wrapper" data-tour="explorer-content-area">
        {/* Adaptive Sidebar - Collapses in single cell mode */}
        <AdaptiveSidebar />

        {/* Main Content Area */}
        <main className="app-main-content">
          {/* Show loading error if any */}
          {loadingError ? (
            <div className="loading-error" style="padding: 40px; text-align: center; color: #ea5b0c;">
              <h2>Failed to Load Cell Data</h2>
              <p>{loadingError}</p>
              <button
                onClick={() => window.location.reload()}
                style="margin-top: 20px; padding: 10px 20px; background: #ea5b0c; color: white; border: none; border-radius: 4px; cursor: pointer;"
              >
                Retry
              </button>
            </div>
          ) : cellParam ? (
            <>
              {console.log('[MainAppPage] Rendering SingleCellView with key:', cellParam)}
              <SingleCellView key={cellParam} cellSlug={cellParam} section={sectionParam} />
            </>
          ) : layout === 'compare' ? (
            /* Compare View - Section-filtered table */
            <CompareTableView />
          ) : mode === 'cells-grid' ? (
            /* Cells Grid View */
            <>
              {GUIDED_SELECTION_ENABLED && (
                <div class="cells-guided-hint">
                  Not sure which cell?{' '}
                  <button
                    type="button"
                    class="cells-guided-hint__link"
                    onClick={() => setLocation('/cell-selection')}
                  >
                    Start a guided selection →
                  </button>
                </div>
              )}
              <ActiveFiltersHeader />
              <GridView
                cells={filteredCells.value}
                loading={isLoading}
                onCellClick={handleCellClick}
                onLockedCellClick={handleLockedCellClick}
              />
            </>
          ) : mode === 'cells-graph' ? (
            /* Graph View (flexible axis selection) */
            <FreePlotView />
          ) : mode === 'cells-table' ? (
            /* Cells Table View */
            <>
              <ActiveFiltersHeader />
              <TableView
                cells={filteredCells.value}
                loading={isLoading}
                onCellClick={handleCellClick}
                onLockedCellClick={handleLockedCellClick}
              />
            </>
          ) : mode.startsWith('benchmarking-') ? (
            /* Cell Selection: the guided-entry landing (two doors — Ion +
               Advanced) when no wizard step is active; the classic
               filter+ranking wizard once a ?step is in the URL. */
            new URLSearchParams(searchParams).has('step') ? (
              <BenchmarkingView />
            ) : (
              <CellSelectionEntry />
            )
          ) : mode.startsWith('radar-') ? (
            /* Technology Radar */
            <RadarContent />
          ) : null}
        </main>
      </div>

    </div>
  )
}

/**
 * Radar Content Component
 * Renders overview or section page based on appViewMode
 */
function RadarContent() {
  const mode = appViewMode.value
  const [location] = useLocation()

  // Extract section ID from reactive location
  const sectionMatch = location.match(/^\/radar\/(.+)$/)
  const sectionId = sectionMatch ? sectionMatch[1] : null

  if (mode === 'radar-section' && sectionId) {
    return <RadarSectionPage sectionId={sectionId} />
  }

  return <RadarOverviewPage />
}

/**
 * Single Cell View Component
 * Renders cell detail with sections
 */
function SingleCellView({ cellSlug, section }) {
  const [productId, setProductId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    console.log('[SingleCellView] useEffect triggered with cellSlug:', cellSlug)
    let attempts = 0
    const maxAttempts = 50

    setIsLoading(true)
    setError(null)
    setProductId(null)

    // Wait for cells to load
    const waitForCells = () => {
      attempts++

      if (allCells.value.length === 0) {
        if (attempts < maxAttempts) {
          console.log('[SingleCellView] Waiting for cells to load... attempt', attempts)
          setTimeout(waitForCells, 100)
          return
        } else {
          console.error('[SingleCellView] Timeout waiting for cells')
          setError('Timeout loading cells')
          setIsLoading(false)
          return
        }
      }

      // Find cell by slug (a pre-#18 legacy slug resolves too, for bookmarked links)
      const cell = findCellBySlug(allCells.value, cellSlug)

      if (!cell) {
        console.error('[SingleCellView] Cell not found for slug:', cellSlug)
        console.error('[SingleCellView] Available cells:', allCells.value.length)
        setError('Cell not found')
        setIsLoading(false)
        return
      }

      console.log('[SingleCellView] Found cell:', cell.manufacturer, cell.name, 'ID:', cell.id)

      // Set productId and let CellPage handle the data loading
      const pid = cell.id || cell.product_id
      setProductId(pid)
      setIsLoading(false)
    }

    waitForCells()
  }, [cellSlug])

  if (isLoading) {
    return (
      <div className="single-cell-loading" style="padding: 40px; text-align: center;">
        <div className="loading-spinner">Loading cell data...</div>
      </div>
    )
  }

  if (error || !productId) {
    return (
      <div className="single-cell-error" style="padding: 40px; text-align: center; color: #ea5b0c;">
        <h2>Error</h2>
        <p>{error || 'Cell not found'}</p>
        <button onClick={() => setLocation('/explorer')} style="margin-top: 20px; padding: 10px 20px; background: #ea5b0c; color: white; border: none; border-radius: 4px; cursor: pointer;">Back to Grid</button>
      </div>
    )
  }

  return (
    <div className="single-cell-view">
      <CellPage key={cellSlug} productId={productId} initialSection={section} />
    </div>
  )
}
